import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { MapPin, Plus, Pencil, Trash2, Crosshair, CheckCircle2 } from 'lucide-react';
import TransportMapLibre from './TransportMapLibre';
import { getBackendErrorMessage } from '../utils/toastError';
import useEscapeKey from '../hooks/useEscapeKey';

function safeTrim(v) {
  return String(v || '').trim();
}

function isValidCoords(c) {
  const lat = Number(c?.lat);
  const lng = Number(c?.lng);
  return Number.isFinite(lat) && Number.isFinite(lng) && lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180;
}

function normalizeLocationFromApi(row) {
  const loc = row?.location && typeof row.location === 'object' ? row.location : {};
  const coords = loc?.coords && typeof loc.coords === 'object' ? loc.coords : null;
  return {
    id: row?.id,
    name: row?.name || '',
    location: {
      label: loc?.label || loc?.address || '',
      address: loc?.address || loc?.label || '',
      coords: coords && coords.lat != null && coords.lng != null ? { lat: Number(coords.lat), lng: Number(coords.lng) } : null,
    },
    created_at: row?.created_at,
    updated_at: row?.updated_at,
  };
}

export default function TransportSavedLocationsModal({
  isOpen,
  onClose,
  onSelect,
  title = 'Mis direcciones',
}) {
  useEscapeKey(onClose, isOpen);
  const [list, setList] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const [mode, setMode] = useState('list'); // list | edit
  const [editingId, setEditingId] = useState(null);
  const [name, setName] = useState('');
  const [coords, setCoords] = useState(null);
  const [label, setLabel] = useState('');
  const [mapsLink, setMapsLink] = useState('');
  const [mapsLinkStatus, setMapsLinkStatus] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  const [isLocating, setIsLocating] = useState(false);
  const [geoError, setGeoError] = useState(null);

  const resetEditor = () => {
    setEditingId(null);
    setName('');
    setCoords(null);
    setLabel('');
    setMapsLink('');
    setMapsLinkStatus(null);
    setSaveError(null);
  };

  const load = async () => {
    setError(null);
    setIsLoading(true);
    try {
      const res = await axios.get('/api/transport/saved-locations');
      const rows = Array.isArray(res.data?.locations) ? res.data.locations : [];
      setList(rows.map(normalizeLocationFromApi));
    } catch (err) {
      // Si es un caso de "sin data" no lo tratamos como error.
      const status = err?.response?.status;
      if (status === 404) {
        setList([]);
        setError(null);
      } else if (status === 500) {
        setList([]);
        setError(null);
      } else {
        setError(getBackendErrorMessage(err, 'No se pudieron cargar tus direcciones'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!isOpen) return;
    setMode('list');
    resetEditor();
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const canSave = useMemo(() => {
    const n = safeTrim(name);
    return n.length > 0 && n.length <= 80 && isValidCoords(coords);
  }, [name, coords]);

  const startCreate = () => {
    resetEditor();
    setMode('edit');
  };

  const startEdit = (row) => {
    const item = row || {};
    setEditingId(item.id || null);
    setName(item.name || '');
    setCoords(item.location?.coords || null);
    setLabel(item.location?.label || item.location?.address || '');
    setMapsLink('');
    setMapsLinkStatus(null);
    setSaveError(null);
    setMode('edit');
  };

  const normalizeMapsLink = async () => {
    const raw = safeTrim(mapsLink);
    setMapsLinkStatus(null);
    setSaveError(null);
    if (!raw) {
      setMapsLinkStatus('Pega un enlace de Google Maps.');
      return;
    }

    setMapsLinkStatus('Normalizando enlace...');
    try {
      const res = await axios.post('/api/ubicacion/normalize-map-link', { url: raw });
      const nextUrl = safeTrim(res.data?.url) || raw;
      const c = res.data?.coords;

      if (c && isValidCoords(c)) {
        setCoords({ lat: Number(c.lat), lng: Number(c.lng) });
        if (!safeTrim(label)) {
          setLabel(nextUrl);
        }
        setMapsLinkStatus('Enlace OK. Ubicación detectada.');
      } else {
        setMapsLinkStatus('Enlace OK, pero no se pudieron extraer coordenadas. Selecciona en el mapa.');
      }
    } catch (err) {
      setMapsLinkStatus(getBackendErrorMessage(err, 'No se pudo normalizar el enlace'));
    }
  };

  const useCurrentLocation = () => {
    setGeoError(null);
    if (!navigator.geolocation) {
      setGeoError('Este dispositivo no soporta geolocalización.');
      return;
    }
    setIsLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const next = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        setCoords(next);
        setLabel(`${next.lat},${next.lng}`);
        setIsLocating(false);
      },
      (err) => {
        const code = err?.code;
        const msg =
          code === 1
            ? 'Permiso de ubicación denegado. Actívalo en Ajustes > Apps > MundoXYZ > Permisos > Ubicación.'
            : code === 2
            ? 'Ubicación no disponible.'
            : code === 3
            ? 'Tiempo de espera agotado al obtener ubicación.'
            : 'No se pudo obtener ubicación.';
        setGeoError(msg);
        setIsLocating(false);
      },
      { enableHighAccuracy: true, maximumAge: 0, timeout: 12000 }
    );
  };

  const save = async () => {
    setSaveError(null);
    if (!canSave) {
      setSaveError('Completa el nombre y selecciona una ubicación válida en el mapa.');
      return;
    }

    const payload = {
      name: safeTrim(name),
      location: {
        label: safeTrim(label) || `${coords.lat},${coords.lng}`,
        address: safeTrim(label) || `${coords.lat},${coords.lng}`,
        coords: { lat: Number(coords.lat), lng: Number(coords.lng) },
      },
    };

    setIsSaving(true);
    try {
      if (editingId) {
        await axios.put(`/api/transport/saved-locations/${editingId}`, payload);
      } else {
        await axios.post('/api/transport/saved-locations', payload);
      }
      await load();
      setMode('list');
      resetEditor();
    } catch (err) {
      setSaveError(getBackendErrorMessage(err, 'No se pudo guardar'));
    } finally {
      setIsSaving(false);
    }
  };

  const remove = async (row) => {
    const id = row?.id;
    if (!id) return;
    const ok = window.confirm('¿Eliminar esta dirección?');
    if (!ok) return;
    try {
      await axios.delete(`/api/transport/saved-locations/${id}`);
      await load();
    } catch (err) {
      setError(getBackendErrorMessage(err, 'No se pudo eliminar'));
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[1700] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
      <div className="bg-[#0b1220] border border-slate-800 rounded-2xl w-full max-w-4xl max-h-[90vh] shadow-2xl shadow-black/50 flex flex-col overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-800">
          <div className="text-slate-100 font-semibold">{title}</div>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-white text-sm px-2 py-1 rounded">
            Cerrar
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-3.5 md:p-4">
          {mode === 'list' ? (
            <div className="space-y-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="text-xs text-slate-400">
                  Guarda tus ubicaciones favoritas para usarlas como Origen/Destino.
                </div>
                <button
                  type="button"
                  onClick={startCreate}
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-blue-600 text-white text-xs font-semibold hover:bg-blue-500"
                >
                  <Plus size={16} />
                  Nueva dirección
                </button>
              </div>

              {isLoading ? <div className="text-sm text-slate-300">Cargando...</div> : null}
              {error ? <div className="text-sm text-red-400">{error}</div> : null}

              {!isLoading && !error && list.length === 0 ? (
                <div className="border border-slate-800 rounded-xl p-4 bg-slate-900 text-slate-300 text-sm">
                  Aún no tienes direcciones guardadas
                </div>
              ) : null}

              <div className="grid gap-2">
                {list.map((row) => {
                  const c = row.location?.coords;
                  const subtitle = row.location?.label || row.location?.address || (c ? `${c.lat},${c.lng}` : '');
                  return (
                    <div key={row.id} className="border border-slate-800 rounded-xl p-3 bg-slate-900">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="text-slate-100 font-semibold break-words">{row.name}</div>
                          <div className="text-[11px] text-slate-400 break-words mt-0.5">{subtitle}</div>
                          {c ? (
                            <div className="text-[11px] text-slate-500 mt-0.5">
                              {Number(c.lat).toFixed(5)}, {Number(c.lng).toFixed(5)}
                            </div>
                          ) : null}
                        </div>
                        <div className="flex flex-wrap justify-end gap-2">
                          <button
                            type="button"
                            onClick={() => onSelect?.({
                              name: row.name,
                              location: row.location,
                            })}
                            disabled={!isValidCoords(c)}
                            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-emerald-600/20 text-emerald-100 border border-emerald-500/30 text-xs font-semibold hover:bg-emerald-600/30 disabled:opacity-60"
                          >
                            <CheckCircle2 size={16} />
                            Usar
                          </button>
                          <button
                            type="button"
                            onClick={() => startEdit(row)}
                            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-800 text-slate-100 border border-slate-700 text-xs font-semibold hover:border-blue-500/60"
                          >
                            <Pencil size={16} />
                            Editar
                          </button>
                          <button
                            type="button"
                            onClick={() => remove(row)}
                            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-red-600/15 text-red-200 border border-red-600/30 text-xs font-semibold hover:bg-red-600/25"
                          >
                            <Trash2 size={16} />
                            Eliminar
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="text-sm text-slate-100 font-semibold inline-flex items-center gap-2">
                  <MapPin size={16} className="text-sky-300" />
                  {editingId ? 'Editar dirección' : 'Nueva dirección'}
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setMode('list');
                    resetEditor();
                  }}
                  className="px-3 py-2 rounded-lg bg-slate-800 text-slate-100 border border-slate-700 text-xs font-semibold hover:border-blue-500/60"
                >
                  Volver
                </button>
              </div>

              <div className="grid md:grid-cols-2 gap-3">
                <div className="space-y-2">
                  <div>
                    <div className="text-[11px] text-slate-400">Nombre</div>
                    <input
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      maxLength={80}
                      className="w-full bg-slate-950 border border-slate-800 rounded-lg px-3 py-2 text-[12px] md:text-sm text-slate-100"
                      placeholder="Ej: Casa, Trabajo, Abuela..."
                    />
                  </div>

                  <div>
                    <div className="text-[11px] text-slate-400">Etiqueta (opcional)</div>
                    <input
                      type="text"
                      value={label}
                      onChange={(e) => setLabel(e.target.value)}
                      className="w-full bg-slate-950 border border-slate-800 rounded-lg px-3 py-2 text-[12px] md:text-sm text-slate-100"
                      placeholder="Referencia / dirección corta"
                    />
                  </div>

                  <div>
                    <div className="text-[11px] text-slate-400">Link de Google Maps (opcional)</div>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={mapsLink}
                        onChange={(e) => setMapsLink(e.target.value)}
                        className="flex-1 bg-slate-950 border border-slate-800 rounded-lg px-3 py-2 text-[12px] md:text-sm text-slate-100"
                        placeholder="Pega aquí un link (incluye enlaces acortados)"
                      />
                      <button
                        type="button"
                        onClick={normalizeMapsLink}
                        className="px-3 py-2 rounded-lg bg-slate-800 text-slate-100 border border-slate-700 text-xs font-semibold hover:border-blue-500/60"
                      >
                        Normalizar
                      </button>
                    </div>
                    {mapsLinkStatus ? <div className="text-[11px] text-slate-400 mt-1">{mapsLinkStatus}</div> : null}
                  </div>

                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={useCurrentLocation}
                      disabled={isLocating}
                      className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-800 text-slate-100 border border-slate-700 text-xs font-semibold hover:border-blue-500/60 disabled:opacity-60"
                    >
                      <Crosshair size={16} />
                      {isLocating ? 'Ubicando...' : 'Ubicación actual'}
                    </button>
                  </div>

                  {geoError ? <div className="text-xs text-red-400">{geoError}</div> : null}

                  <div className="text-[11px] text-slate-400">
                    Toca el mapa para colocar el pin.
                  </div>
                </div>

                <div className="h-72 w-full overflow-hidden rounded-lg border border-slate-800 bg-slate-900">
                  <TransportMapLibre
                    view="destination"
                    pickEnabled={true}
                    pickedCoords={coords}
                    onPick={(c) => {
                      if (!c) return;
                      setCoords({ lat: Number(c.lat), lng: Number(c.lng) });
                      setGeoError(null);
                    }}
                  />
                </div>
              </div>

              {coords ? (
                <div className="text-xs text-slate-300">
                  Coordenadas: <span className="font-mono">{Number(coords.lat).toFixed(6)}, {Number(coords.lng).toFixed(6)}</span>
                </div>
              ) : null}

              {saveError ? <div className="text-sm text-red-400">{saveError}</div> : null}

              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={save}
                  disabled={isSaving || !canSave}
                  className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-semibold hover:bg-blue-500 disabled:opacity-60"
                >
                  {isSaving ? 'Guardando...' : 'Guardar'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setMode('list');
                    resetEditor();
                  }}
                  className="px-4 py-2 rounded-lg bg-slate-800 border border-slate-700 text-slate-100 text-sm hover:border-blue-500/60"
                >
                  Cancelar
                </button>
              </div>
            </div>
          )}
        </div>
        <div className="border-t border-slate-800 px-4 py-3 bg-[#0b1220]">
          <button
            type="button"
            onClick={onClose}
            className="w-full px-4 py-3 rounded-xl bg-slate-800 text-slate-100 border border-slate-700 text-sm font-semibold hover:border-blue-500/60"
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
}
